import {ReactNode, useState} from 'react';
import {
    Box,
    Button,
    ButtonProps,
    Center,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    HStack,
    TableContainer,
    VisuallyHidden
} from '@chakra-ui/react';
import {TableColumn} from "../../models";
import {scrollbar} from "../styling";

type ActionsProps = {
    current: number
    onUpdate: (v:number) => void
    total: number
};
type GenericTableProps<T> = {
    data: T[];
    columns: TableColumn<T>[];
    pageSize?: number;
    emptyMessage?: ReactNode;
    onRowClick?: (val: T) => void
    rowKey?: (val: T) => any
};
const px={base: "4px", md: "12px"};
function GenericPaginatedTable<T>(p: GenericTableProps<T>) {
    const size = p.pageSize ?? 10;
    const [currentPage, setCurrentPage] = useState(1);
    const total = Math.ceil(p.data.length / size);
    const data = p.data.slice( (currentPage - 1) * size, currentPage * size);
    function handleRowClick(val: T) {
        if (p.onRowClick) {
            p.onRowClick(val);
        }
    }
    function columnWidth() {
        return {
            base: `max(100px, calc(100% / ${p.columns.length}))`,
            md: `max(150px, calc(100% / ${p.columns.length}))`
        };
    }

    return <>
        <TableContainer border="1px solid #EBEEF2" rounded="md" p=".5rem 0 0" color="#1F1F1F" overflowY="auto"  gap={"1rem"} flex="2 1 auto" display="flex" flexDir="column" justifyContent="space-between" position="relative">
            <Box sx={scrollbar.hidden} overflowX="auto" maxW="full">
                <Table variant="simple" colorScheme="gray" size="md">
                    <Thead>
                        <Tr>{p.columns.map(
                            (col, idx) => <Th p={px} key={idx} minW={columnWidth()}>{col.header}</Th>
                        )}</Tr>
                    </Thead>
                    <Tbody>
                        {data.map((row, i) => (
                            <Tr
                                key={p.rowKey ? p.rowKey(row) : i}
                                onClick={() => handleRowClick(row)}
                                _hover={{cursor: p.onRowClick ? "pointer" : "default"}}
                            >
                                {p.columns.map((col, j) => (
                                    <Td
                                        key={j}
                                        p={{base: "4px", md: `4px 4px 4px ${px.md}`}}
                                        minW={columnWidth()}
                                        whiteSpace="break-spaces"
                                        lineHeight="1.1"
                                        borderBottom="1px solid #EBEEF2"
                                        style={{textWrap: "balance"}}
                                    >
                                        {typeof col.accessor === 'function'
                                            ? col.accessor(row)
                                            : (row as any)[col.accessor]}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            {total === 0 && <Center minH="200px" color="gray.500" position="absolute" insetInline="0" insetBlock="2px">
                {p.emptyMessage ?? "No Data Available."}
            </Center>}
            {total > 0 && <TableActions current={currentPage} onUpdate={setCurrentPage} total={total}/>}
        </TableContainer>
    </>;
}

function PageButton(props: ButtonProps & {currentStep?: boolean}) {
    const p = Object.assign({}, props);
    delete p.currentStep;
    return <Button
        {...p}
        minW={props.minW ?? "32px"}
        colorScheme={props.currentStep ? "primary": undefined}
        p="4px"
        transition="all .35s ease"
    >
        {props.children}
    </Button>;
}


function generatePages(current: number, total: number): number[] {
    let length: number = 5;
    let initial: number = 1;
    if (total <= 5) {
        length = total;
    }
    if (current > 3 && total - current > 2) {
        initial = current - 2;
    }
    if (total - current <= 2) {
        initial = total - length + 1;
    }
    return Array.from ({length}, (_, i) => initial + i);
}

function TableActions(p: ActionsProps) {
    const prev = () => p.onUpdate(p.current - 1);
    const next = () => p.onUpdate(p.current + 1);
    const pages = generatePages(p.current, p.total);
    return <HStack justify="center" my={4} mx={4} gap={4}>
        <PageButton onClick={prev} isDisabled={p.current === 1} aria-labelledby="table-btn-prev" variant="outline">
            <svg width="24" height="24" aria-hidden="true">
                <use xlinkHref="#chevron_left" />
            </svg>
            <VisuallyHidden id="table-btn-prev">Previous Page</VisuallyHidden>
        </PageButton>
        <HStack gap={2}>
            {pages[0] !== 1 && <PageButton onClick={() => p.onUpdate(1)}>...</PageButton>}
            {
                pages.map((num) => <PageButton key={num} currentStep={num === p.current} onClick={() => p.onUpdate(num)} >
                    {num}
                </PageButton>)
            }
            {pages.at(-1) !== p.total && <PageButton onClick={() => p.onUpdate(p.total)}>...</PageButton>}
        </HStack>
        <PageButton onClick={next} isDisabled={p.current === p.total} aria-labelledby="table-btn-next" variant="outline" w="32px" px="4px">
            <svg width="24" height="24" aria-hidden="true">
                <use xlinkHref="#chevron_right" />
            </svg>
            <VisuallyHidden id="table-btn-next">Next Page</VisuallyHidden>
        </PageButton>
    </HStack>;
}

export default GenericPaginatedTable;
